import { useCallback, useEffect, useRef } from 'react';

/** What a save needs from an editor's state: whether there is anything to save, and how to mark it saved. */
export interface SaveableState {
  isDirty: boolean;
  markSaved: () => void;
}

/** The host's side of saving: where saves go, and who hears the unsaved-changes flag. */
export interface SaveHost<Payload> {
  /** Takes each save. Without it nothing saves. */
  onSave?: (payload: Payload) => void;
  /** Hears each change of the unsaved-changes flag, for a host's leave guard. */
  onDirtyChange?: (isDirty: boolean) => void;
  readOnly: boolean;
}

/**
 * THE save for an editor root, the same in every shell: the Save button and
 * the host's `ref.save()` both run the function this returns. It saves only
 * when the host takes saves, the editor is editable, and there are unsaved
 * changes. It builds the `payload` from the version shown, marks that version
 * saved, and hands the payload to `onSave`. It returns true when it saved.
 */
export function useSaveHandle<Payload>(state: SaveableState, host: SaveHost<Payload>, payload: () => Payload): () => boolean {
  const latest = useRef({ state, host, payload });
  latest.current = { state, host, payload };

  const { isDirty } = state;
  useEffect(() => {
    latest.current.host.onDirtyChange?.(isDirty);
  }, [isDirty]);

  return useCallback(() => {
    const { state: current, host: currentHost, payload: build } = latest.current;
    if (!currentHost.onSave || currentHost.readOnly || !current.isDirty) return false;
    const saved = build();
    current.markSaved();
    currentHost.onSave(saved);
    return true;
  }, []);
}
